import { Component, OnInit, Input } from '@angular/core';
import { WeatherStation } from 'src/api/models';

import * as Highcharts from 'highcharts';
import more from "highcharts/highcharts-more";
more(Highcharts);

@Component({
  selector: 'app-windrose-home',
  templateUrl: './windrose-home.component.html',
  styleUrls: ['./windrose-home.component.scss']
})
export class WindroseHomeComponent implements OnInit {

  @Input() weatherStation: WeatherStation;

  public waterTally: number;

  // Highcharts
  public Highcharts: typeof Highcharts = Highcharts;
  public chartOptions: Highcharts.Options = {};
  public chartCallback: Highcharts.ChartCallbackFunction;
  public updateFlag: boolean = false;
  public oneToOneFlag: boolean = true;


  private readonly Debug = false;
  private readonly GroupSize = 5;

  constructor() {}

  ngOnInit() {
    this.chartCallback = (chart) => this.buildData(chart);

    this.tallyWater();
    
    // Chart Options
    this.chartOptions = {
      colors: ['#4572A7', '#AA4643', '#89A54E', '#80699B', '#3D96AE', '#DB843D', '#92A8CD', '#A47D7C', '#B5CA92'],
      chart: {
        polar: true,
        type: 'column',
        backgroundColor: 'transparent',
        spacing: [5,25,5,25],
        height: 250,
        width: 250,
        //reflow: true
      },
      title: {
        text: ""
      },
      legend: {
        enabled: false,
        borderWidth: 0,
        margin: 5
      },
      tooltip: {
        pointFormat: '<span style="color:{point.color}">●</span> {series.name}: <b>{point.p}%</b><br/>',
        followPointer: true,
        shared: true
      },
      navigation:{
        buttonOptions:{
          enabled: false
        }
      },
      credits: {
        enabled: false
      },
      xAxis: {        
        tickmarkPlacement: 'on',
        categories: this.getDirections(),
        labels:{
          style:{
            color:'rgba(255, 255, 255, 0.6)'
          },
        }
      },
      yAxis: {
        min: 0,
        endOnTick: false,
        showLastLabel: true,
        title: {
          text: ''
        },
        labels: {
          enabled: false,
          style:{
            color:'rgba(255, 255, 255, 0.6)'
          },
          formatter: function() {
            return this.value + '%';
          }
        },
        reversedStacks: false
      },
      plotOptions: {
        series: {
          stacking: 'normal',
          shadow: false,
          pointPlacement: 'on'
        },
      },
      responsive: {
        rules: [{
            condition: {
                maxWidth: 250
            },
            chartOptions: {
                legend: {
                    align: 'center',
                    verticalAlign: 'bottom',
                    layout: 'horizontal'
                },
                yAxis: {
                    labels: {
                        align: 'left',
                        x: 0,
                        y: -5
                    },
                    title: {
                        text: null
                    }
                },
                subtitle: {
                    text: null
                },
                credits: {
                    enabled: false
                }
            }
        }]
      }
    };
  } 

  public isWeatherData(): boolean {
    return this.weatherStation.weatherQueue && this.weatherStation.weatherQueue.length > 0;
  }

  public tallyWater() {
    if (this.isWeatherData() && this.weatherStation.supportsRain) {
      let tally: number = 0;
      let queue = this.weatherStation.weatherQueue;
      
      for (let i = 0; i < queue.length; i++) {
        tally += queue[i].value.rain;
      }

      this.waterTally = tally;
    }    
  }

  private buildData(chart) {
    // can only build data if weather station supports wind, and there is data in the queue
    if (this.isWeatherData() && this.weatherStation.supportsWind) {
      // Data
      let weatherStationData = this.weatherStation.weatherQueue;    
      let directions: string[] = this.getDirections();
      let windData: any = []
      let speeds: number[] = []

      if (this.Debug) {
        for (let i = 0; i < 20; i++) {
          weatherStationData.push({
            lastChanged: "",
            timeStamp: "",
            lastRaised: "",
            value: {
              rain: 0,
              windDirection: Math.floor(Math.random() * 360),
              windSpeed: Math.random() * 20
            }
          })
        }
      }

      // Sort Data based on wind speed
      weatherStationData = weatherStationData.sort((a, b) =>  a.value.windSpeed - b.value.windSpeed);

      // Group like speeds together
      for (let i = 0; i < weatherStationData.length; i++) {
        let inLoop = weatherStationData[i];

        let speedCategory = this.getSpeedCategory(inLoop.value.windSpeed);
        windData.push({ speed: speedCategory, direction: inLoop.value.windDirection, timeStamp: inLoop.timeStamp})
        if (!speeds.includes(speedCategory)) {
          speeds.push(speedCategory);
        }
      }

      // group by wind speed
      for (let i = 0; i < speeds.length; i++) {
        const speed = speeds[i];
        const chartData = { name : `${speed} - ${speed + this.GroupSize}`, data:[]};
        // find all matches
        const matchingSpeeds = windData.filter(w => w.speed == speed);

        // loops each direction, finding speed
        for (let j = 0; j < directions.length; j++) {
          const direction = directions[j];
          const allSpeedsByDirection = windData.filter(w => this.getDirectionByDegree(w.direction) === direction);
          const matchingSpeedsByDirection = matchingSpeeds.filter(w => this.getDirectionByDegree(w.direction) === direction);

          const speedOcurranceOverall = Math.round(matchingSpeedsByDirection.length / matchingSpeeds.length * 100) || 0;
          const speedOccurangeByDirection: number = Math.round(matchingSpeedsByDirection.length / allSpeedsByDirection.length * 100) || 0;

          chartData.data.push({ y: speedOcurranceOverall, p: speedOccurangeByDirection } as any);

          // debug
          // console.log(`Overall - ${direction} (${speed}): ${matchingSpeedsByDegrees.length} is what percentage of ${matchingSpeeds.length} = ${speedOcurranceOverall}`);
          // console.log(`Direction - ${direction} (${speed}): ${matchingSpeedsByDegrees.length} is what percentage of ${speedsByDegrees.length} = ${speedOccurangeByDirection}`);
          // console.log("--------------------")
        }

        // add data group by speed to chart
        chart.addSeries(chartData);
      }
    }
  }

  private getSpeedCategory(speed: number): number { 
    return Math.round(speed / this.GroupSize) * this.GroupSize;
  }

  private getDirections(): string[] {
    return  ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
  }

  /**
   * Convert cardinal direction based on array index to degrees
   */
  private getDirectionByDegree(degrees: number): string {
    let i = (degrees + 11.25)/22.5;
    let directions = this.getDirections();
    let directionInDegrees = directions[Math.floor(i % 16)];
    return directionInDegrees;
  }

}
